import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Sparkle, FileText, Target, Copy, ChartLineUp } from "@phosphor-icons/react";

function CopyBtn({ text }) {
  return (
    <button onClick={() => { navigator.clipboard.writeText(text); toast.success("Copied"); }}
            className="text-[10px] px-1.5 py-0.5 border border-zinc-200 rounded-sm hover:bg-zinc-50 inline-flex items-center gap-1">
      <Copy size={10} /> copy
    </button>
  );
}

function JDTab() {
  const [form, setForm] = useState({ title: "", department: "", level: "", location: "", brief: "", industry: "" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const gen = async () => {
    if (!form.title.trim()) { toast.error("Role title required"); return; }
    setBusy(true);
    try { const { data } = await api.post("/ai-gen/jd", form); setResult(data); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const asText = result ? [
    result.summary, "",
    "Key responsibilities:", ...(result.responsibilities || []).map(r => "• " + r), "",
    "Must-have:", ...(result.requirements_must || []).map(r => "• " + r), "",
    "Nice-to-have:", ...(result.requirements_good_to_have || []).map(r => "• " + r), "",
    "Qualifications:", ...(result.qualifications || []).map(r => "• " + r), "",
    "Benefits:", ...(result.benefits || []).map(r => "• " + r), "",
    "Interview process:", ...(result.interview_process || []).map(r => "• " + r),
  ].join("\n") : "";

  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="border border-zinc-200 rounded-sm bg-white p-4 space-y-3">
        <div className="text-xs uppercase tracking-widest font-bold text-zenith-teal">Brief</div>
        <div><Label className="text-xs">Role title *</Label>
          <Input data-testid="ai-jd-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="rounded-sm mt-1" placeholder="e.g. Senior Product Designer" /></div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">Department</Label>
            <Input value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} className="rounded-sm mt-1" /></div>
          <div><Label className="text-xs">Level</Label>
            <Input value={form.level} onChange={e => setForm({ ...form, level: e.target.value })} className="rounded-sm mt-1" placeholder="e.g. L3, Manager" /></div>
          <div><Label className="text-xs">Location</Label>
            <Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} className="rounded-sm mt-1" /></div>
          <div><Label className="text-xs">Industry</Label>
            <Input value={form.industry} onChange={e => setForm({ ...form, industry: e.target.value })} className="rounded-sm mt-1" placeholder="Fintech / SaaS / …" /></div>
        </div>
        <div><Label className="text-xs">Extra brief (optional)</Label>
          <Textarea rows={3} value={form.brief} onChange={e => setForm({ ...form, brief: e.target.value })} className="rounded-sm mt-1" placeholder="Anything HR must call out — team size, tech stack, must-do outcomes…" /></div>
        <Button onClick={gen} disabled={busy} data-testid="ai-jd-generate" className="w-full rounded-sm bg-zenith-magenta hover:bg-zenith-magenta/90 text-white">
          <Sparkle size={14} weight="fill" className="mr-1" /> {busy ? "Drafting…" : "Generate JD"}
        </Button>
      </div>

      <div className="border border-zinc-200 rounded-sm bg-white p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-widest font-bold text-zenith-magenta">Draft</div>
          {result && <CopyBtn text={asText} />}
        </div>
        {!result ? (
          <div className="text-xs text-zinc-500 border border-dashed border-zinc-200 rounded-sm p-6 text-center">
            AI-drafted JD will appear here. Fill the brief and click Generate.
          </div>
        ) : (
          <div className="text-sm space-y-3">
            <div className="italic text-zinc-700">{result.summary}</div>
            {["responsibilities","requirements_must","requirements_good_to_have","qualifications","benefits","interview_process"].map(k => (
              (result[k] || []).length > 0 && (
                <div key={k}>
                  <div className="text-xs uppercase tracking-widest font-bold text-adb-navy mt-2">{k.replace(/_/g, " ")}</div>
                  <ul className="list-disc pl-5 mt-1 text-sm space-y-0.5">{result[k].map((r,i) => <li key={i}>{r}</li>)}</ul>
                </div>
              )
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function KRATab() {
  const [form, setForm] = useState({ role_title: "", department: "", level: "", business_context: "", n_kras: 4, kpis_per_kra: 3 });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const gen = async () => {
    if (!form.role_title.trim()) { toast.error("Role title required"); return; }
    setBusy(true);
    try { const { data } = await api.post("/ai-gen/kras", { ...form, n_kras: parseInt(form.n_kras) || 4, kpis_per_kra: parseInt(form.kpis_per_kra) || 3 }); setResult(data); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const asJSON = result ? JSON.stringify(result, null, 2) : "";

  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="border border-zinc-200 rounded-sm bg-white p-4 space-y-3">
        <div className="text-xs uppercase tracking-widest font-bold text-zenith-teal">Brief</div>
        <div><Label className="text-xs">Role title *</Label>
          <Input data-testid="ai-kra-role" value={form.role_title} onChange={e => setForm({ ...form, role_title: e.target.value })} className="rounded-sm mt-1" placeholder="e.g. Head of Sales" /></div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">Department</Label>
            <Input value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} className="rounded-sm mt-1" /></div>
          <div><Label className="text-xs">Level</Label>
            <Input value={form.level} onChange={e => setForm({ ...form, level: e.target.value })} className="rounded-sm mt-1" /></div>
          <div><Label className="text-xs"># KRAs</Label>
            <Input type="number" value={form.n_kras} onChange={e => setForm({ ...form, n_kras: e.target.value })} className="rounded-sm mt-1 font-mono-plex" /></div>
          <div><Label className="text-xs">KPIs per KRA</Label>
            <Input type="number" value={form.kpis_per_kra} onChange={e => setForm({ ...form, kpis_per_kra: e.target.value })} className="rounded-sm mt-1 font-mono-plex" /></div>
        </div>
        <div><Label className="text-xs">Business context (optional)</Label>
          <Textarea rows={3} value={form.business_context} onChange={e => setForm({ ...form, business_context: e.target.value })} className="rounded-sm mt-1" placeholder="e.g. Series B SaaS growing 3× YoY, north-star metric = ARR" /></div>
        <Button onClick={gen} disabled={busy} data-testid="ai-kra-generate" className="w-full rounded-sm bg-zenith-magenta hover:bg-zenith-magenta/90 text-white">
          <Sparkle size={14} weight="fill" className="mr-1" /> {busy ? "Drafting…" : "Generate KRAs + KPIs"}
        </Button>
      </div>

      <div className="border border-zinc-200 rounded-sm bg-white p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-widest font-bold text-zenith-magenta">Draft scorecard</div>
          {result && <CopyBtn text={asJSON} />}
        </div>
        {!result ? (
          <div className="text-xs text-zinc-500 border border-dashed border-zinc-200 rounded-sm p-6 text-center">
            AI will draft weighted KRAs and SMART KPIs. Total weight = 100%.
          </div>
        ) : (
          <div className="space-y-3">
            {(result.kras || []).map((k, i) => (
              <div key={i} data-testid={`ai-kra-block-${i}`} className="border border-zenith-teal/30 bg-zenith-teal/5 rounded-sm p-3">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-zenith-teal-700 font-bold">KRA {i + 1} · Weight {k.weight_percent}%</div>
                    <div className="font-display text-lg font-bold text-adb-navy mt-0.5">{k.area}</div>
                  </div>
                </div>
                <div className="text-xs text-zinc-600 mt-1 italic">{k.description}</div>
                <div className="mt-2 space-y-1">
                  {(k.kpis || []).map((kp, j) => (
                    <div key={j} className="border-l-2 border-zenith-magenta pl-2 py-1 bg-white text-xs">
                      <div className="font-semibold">{kp.label} <span className="text-zinc-400 font-normal">({kp.weight_percent}%)</span></div>
                      <div className="text-[11px] text-zinc-600">Metric: {kp.metric} · Target: {kp.target}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function KPITab() {
  const [form, setForm] = useState({ kra_area: "", role_title: "", n: 3 });
  const [result, setResult] = useState([]);
  const [busy, setBusy] = useState(false);
  const gen = async () => {
    if (!form.kra_area.trim()) { toast.error("KRA area required"); return; }
    setBusy(true);
    try { const { data } = await api.post("/ai-gen/kpis", { ...form, n: parseInt(form.n) || 3 }); setResult(data); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="border border-zinc-200 rounded-sm bg-white p-4 space-y-3">
        <div className="text-xs uppercase tracking-widest font-bold text-zenith-teal">Brief</div>
        <div><Label className="text-xs">KRA area *</Label>
          <Input data-testid="ai-kpi-area" value={form.kra_area} onChange={e => setForm({ ...form, kra_area: e.target.value })} className="rounded-sm mt-1" placeholder="e.g. Customer retention" /></div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">Role (optional)</Label>
            <Input value={form.role_title} onChange={e => setForm({ ...form, role_title: e.target.value })} className="rounded-sm mt-1" /></div>
          <div><Label className="text-xs"># KPIs</Label>
            <Input type="number" value={form.n} onChange={e => setForm({ ...form, n: e.target.value })} className="rounded-sm mt-1 font-mono-plex" /></div>
        </div>
        <Button onClick={gen} disabled={busy} data-testid="ai-kpi-generate" className="w-full rounded-sm bg-zenith-magenta hover:bg-zenith-magenta/90 text-white">
          <Sparkle size={14} weight="fill" className="mr-1" /> {busy ? "Drafting…" : "Generate KPIs"}
        </Button>
      </div>
      <div className="border border-zinc-200 rounded-sm bg-white p-4 space-y-2">
        <div className="text-xs uppercase tracking-widest font-bold text-zenith-magenta">SMART KPIs</div>
        {result.length === 0 ? (
          <div className="text-xs text-zinc-500 border border-dashed border-zinc-200 rounded-sm p-6 text-center">Type a KRA area and click Generate.</div>
        ) : (
          result.map((kp, i) => (
            <div key={i} data-testid={`ai-kpi-block-${i}`} className="border-l-2 border-zenith-magenta pl-2 py-1 bg-white text-xs">
              <div className="font-semibold">{kp.label} <span className="text-zinc-400 font-normal">({kp.weight_percent}%)</span></div>
              <div className="text-[11px] text-zinc-600">Metric: {kp.metric} · Target: {kp.target}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}


export default function AIGenerator() {
  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-zenith-magenta font-bold">AI Studio</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">JD, KRA and KPI in 30 seconds.</h1>
        <p className="text-sm text-zinc-500 mt-2 max-w-2xl">Give a role title (and a hint of what your business needs) — the AI drafts a full job description, weighted KRAs and SMART KPIs. Copy, tweak, ship.</p>
      </div>
      <Tabs defaultValue="jd">
        <TabsList className="bg-zinc-100 border border-zinc-200 rounded-sm">
          <TabsTrigger value="jd" className="rounded-sm" data-testid="tab-ai-jd"><FileText size={12} className="mr-1" /> Job Description</TabsTrigger>
          <TabsTrigger value="kra" className="rounded-sm" data-testid="tab-ai-kra"><Target size={12} className="mr-1" /> KRAs + KPIs</TabsTrigger>
          <TabsTrigger value="kpi" className="rounded-sm" data-testid="tab-ai-kpi"><ChartLineUp size={12} className="mr-1" /> KPIs only</TabsTrigger>
        </TabsList>
        <TabsContent value="jd" className="mt-4"><JDTab /></TabsContent>
        <TabsContent value="kra" className="mt-4"><KRATab /></TabsContent>
        <TabsContent value="kpi" className="mt-4"><KPITab /></TabsContent>
      </Tabs>
    </div>
  );
}
